import { Fragment, type ReactNode, useEffect, useState } from 'react';
import {
  Link,
  NavLink,
  Navigate,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DashboardShell } from '../../components/DashboardShell.js';
import { api, ApiClientError } from '../../lib/api.js';
import { useAuthStore } from '../../lib/auth.js';

export function AdminPage() {
  const user = useAuthStore((s) => s.user);
  const { lang } = useParams();
  const { t } = useTranslation('dashboard');

  if (!user?.isAdmin) {
    // Hard redirect away — non-admins shouldn't be able to even attempt the
    // admin endpoints (the API enforces this anyway, but the UI should not
    // suggest the area exists).
    return <Navigate to={`/${lang}/dashboard`} replace />;
  }

  return (
    <DashboardShell>
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-3xl font-bold">Admin</h1>
        <span className="text-xs uppercase tracking-wider text-emerald-300 border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 rounded-full">
          {t('admin.platform_badge')}
        </span>
      </div>
      <p className="text-neutral-400 text-sm mb-6">{t('admin.intro')}</p>

      <nav className="flex gap-1 border-b border-neutral-900 mb-6 overflow-x-auto">
        <AdminTab to={`/${lang}/dashboard/admin`} end>
          {t('admin.tabs.overview')}
        </AdminTab>
        <AdminTab to={`/${lang}/dashboard/admin/users`}>{t('admin.tabs.users')}</AdminTab>
        <AdminTab to={`/${lang}/dashboard/admin/orgs`}>{t('admin.tabs.orgs')}</AdminTab>
        <AdminTab to={`/${lang}/dashboard/admin/events`}>{t('admin.tabs.events')}</AdminTab>
        <AdminTab to={`/${lang}/dashboard/admin/orders`}>{t('admin.tabs.orders')}</AdminTab>
      </nav>

      <Routes>
        <Route index element={<OverviewTab />} />
        <Route path="users" element={<UsersTab />} />
        <Route path="users/:userId" element={<UserDetail />} />
        <Route path="orgs" element={<OrgsTab />} />
        <Route path="events" element={<EventsTab />} />
        <Route path="orders" element={<OrdersTab />} />
        <Route path="*" element={<Outlet />} />
      </Routes>
    </DashboardShell>
  );
}

function AdminTab({ to, end, children }: { to: string; end?: boolean; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        [
          'px-3 py-2 text-sm border-b-2 -mb-px transition whitespace-nowrap',
          isActive
            ? 'border-[var(--color-accent)] text-neutral-100'
            : 'border-transparent text-neutral-500 hover:text-neutral-300',
        ].join(' ')
      }
    >
      <>{children}</>
    </NavLink>
  );
}

/**
 * Footer compartido de los listados paginados del admin: muestra el total y un
 * botón "Cargar más" cuando hay más páginas. La paginación es acumulativa
 * (useInfiniteQuery): cada click trae la siguiente página y la concatena.
 */
function LoadMoreFooter({
  label,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: {
  label: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="flex items-center justify-between mt-4 gap-3">
      <p className="text-xs text-neutral-500">{label}</p>
      {hasNextPage && (
        <button
          type="button"
          onClick={onLoadMore}
          disabled={isFetchingNextPage}
          className="text-xs px-3 py-1.5 rounded border border-neutral-700 text-neutral-300 hover:text-neutral-100 hover:border-neutral-500 transition disabled:opacity-50"
        >
          {isFetchingNextPage ? '…' : t('admin.load_more')}
        </button>
      )}
    </div>
  );
}

interface Overview {
  users: { total: number; admins: number };
  orgs: { total: number; stripeConnected: number; pending: number };
  events: { live: number; draft: number; total: number };
  tickets: { issued: number; validated: number };
  orders: { paid: number; grossCents: number };
  credits: { purchased: number; consumed: number };
}

function OverviewTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const overview = useQuery<Overview>({
    queryKey: ['admin', 'overview'],
    queryFn: () => api<Overview>('/admin/overview'),
    refetchOnWindowFocus: false,
  });

  if (overview.isLoading) {
    return <p className="text-neutral-500 text-sm">{tCommon('messages.loading')}</p>;
  }
  if (overview.isError || !overview.data) {
    return (
      <p className="text-red-400 text-sm">
        {(overview.error as ApiClientError | undefined)?.message ?? 'Failed to load'}
      </p>
    );
  }

  const o = overview.data;
  const fmt = new Intl.NumberFormat(i18n.language);
  const fmtUsd = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'USD',
  });

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      <Stat
        label={t('admin.overview.users')}
        value={fmt.format(o.users.total)}
        sub={t('admin.overview.users_sub', { count: o.users.admins })}
      />
      <Stat
        label={t('admin.overview.orgs')}
        value={fmt.format(o.orgs.total)}
        sub={t('admin.overview.orgs_sub', { count: o.orgs.stripeConnected })}
      />
      <Stat
        label={t('admin.overview.orgs_pending')}
        value={fmt.format(o.orgs.pending)}
        sub={t('admin.overview.orgs_pending_sub')}
        highlight={o.orgs.pending > 0}
      />
      <Stat
        label={t('admin.overview.live_events')}
        value={fmt.format(o.events.live)}
        sub={t('admin.overview.live_events_sub', { total: o.events.total, draft: o.events.draft })}
      />
      <Stat
        label={t('admin.overview.paid_orders')}
        value={fmt.format(o.orders.paid)}
        sub={fmtUsd.format(o.orders.grossCents / 100)}
      />
      <Stat
        label={t('admin.overview.tickets_issued')}
        value={fmt.format(o.tickets.issued)}
        sub={t('admin.overview.tickets_issued_sub', { count: o.tickets.validated })}
      />
      <Stat label={t('admin.overview.credits_purchased')} value={fmt.format(o.credits.purchased)} />
      <Stat label={t('admin.overview.credits_consumed')} value={fmt.format(o.credits.consumed)} />
      <Stat
        label={t('admin.overview.pending_tickets')}
        value={fmt.format(Math.max(0, o.tickets.issued - o.tickets.validated))}
      />
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  highlight,
}: {
  label: string;
  value: string;
  sub?: string;
  highlight?: boolean;
}) {
  return (
    <div className={`card ${highlight ? 'border-amber-500/40 bg-amber-500/5' : ''}`}>
      <p className="text-xs uppercase tracking-wider text-neutral-500">{label}</p>
      <p
        className={`text-2xl sm:text-3xl font-bold mt-2 break-all ${
          highlight ? 'text-amber-300' : ''
        }`}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-neutral-500 mt-1">{sub}</p>}
    </div>
  );
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
  locale: 'es' | 'en';
  country: string | null;
  isAdmin: boolean;
  memberships: number;
  createdAt: string;
}

function UsersTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { lang } = useParams();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ['admin', 'users', debouncedQ],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ pageSize: '50', page: String(pageParam) });
      if (debouncedQ) params.set('q', debouncedQ);
      return api<{ users: AdminUser[]; total: number; hasMore: boolean; page: number }>(
        `/admin/users?${params.toString()}`,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    refetchOnWindowFocus: false,
  });
  const users = list.data?.pages.flatMap((p) => p.users) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;

  const toggle = useMutation({
    mutationFn: ({ id, isAdmin }: { id: string; isAdmin: boolean }) =>
      api<{ id: string; isAdmin: boolean }>(`/admin/users/${id}/admin`, {
        method: 'PATCH',
        body: { isAdmin },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });

  return (
    <div>
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={t('admin.users_tab.search_placeholder')}
        className="input mb-4 max-w-md"
      />
      {list.isLoading ? (
        <p className="text-neutral-500 text-sm">{tCommon('messages.loading')}</p>
      ) : users.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left pb-3 pr-4">Email</th>
                <th className="text-left pb-3 pr-4">{t('admin.users_tab.name_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.users_tab.country_col')}</th>
                <th className="text-right pb-3 pr-4">{t('admin.users_tab.orgs_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.users_tab.created_col')}</th>
                <th className="text-right pb-3">Admin</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr
                    key={u.id}
                    className="border-t border-neutral-900 hover:bg-neutral-900/30 transition"
                  >
                    <td className="py-2 pr-4 text-neutral-200 break-all">
                      <Link
                        to={`/${lang}/dashboard/admin/users/${u.id}`}
                        className="hover:text-[var(--color-accent)] underline underline-offset-2 decoration-neutral-700 hover:decoration-[var(--color-accent)]"
                      >
                        {u.email}
                      </Link>
                    </td>
                    <td className="py-2 pr-4 text-neutral-300">{u.name || '—'}</td>
                    <td className="py-2 pr-4 text-neutral-400 uppercase text-xs">
                      {u.country ?? '—'}
                    </td>
                    <td className="py-2 pr-4 text-right text-neutral-400">{u.memberships}</td>
                    <td className="py-2 pr-4 text-neutral-500">
                      {new Date(u.createdAt).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        onClick={() => toggle.mutate({ id: u.id, isAdmin: !u.isAdmin })}
                        disabled={isSelf || toggle.isPending}
                        className={[
                          'text-xs px-2 py-1 rounded-full border transition',
                          u.isAdmin
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 hover:bg-emerald-500/25'
                            : 'bg-neutral-900 text-neutral-400 border-neutral-800 hover:text-neutral-200',
                          isSelf ? 'opacity-50 cursor-not-allowed' : '',
                        ].join(' ')}
                        title={isSelf ? t('admin.users_tab.self_role_title') : undefined}
                      >
                        {u.isAdmin ? '✓ Admin' : t('admin.users_tab.promote')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <LoadMoreFooter
            label={t('admin.users_tab.total', { count: total })}
            hasNextPage={list.hasNextPage}
            isFetchingNextPage={list.isFetchingNextPage}
            onLoadMore={() => list.fetchNextPage()}
          />
        </div>
      ) : (
        <p className="text-neutral-500 text-sm">{tCommon('messages.no_results')}</p>
      )}
    </div>
  );
}

interface AdminOrg {
  id: string;
  name: string;
  slug: string;
  ownerEmail: string | null;
  ownerName: string | null;
  stripeAccountId: string | null;
  stripeAccountUrl: string | null;
  stripeOnboardingComplete: boolean;
  creditsBalance: number;
  freeMonthlyAllowance: number;
  freeMonthlyConsumed: number;
  suspendedAt: string | null;
  suspensionReason: string | null;
  reviewStatus: 'pending' | 'approved' | 'rejected';
  reviewedAt: string | null;
  reviewNotes: string | null;
  createdAt: string;
}

interface MoneyRow {
  currency: string;
  amountCents: number;
}
interface StripeBalanceResponse {
  connected: boolean;
  accountId: string | null;
  error: boolean;
  available: MoneyRow[];
  pending: MoneyRow[];
  payoutsPaid: MoneyRow[];
  payoutsInTransit: MoneyRow[];
}

/**
 * Panel on-demand con el balance y payouts de la cuenta Stripe Connect de una
 * org. Cuantifica la exposición de fraude: "disponible" es recuperable AHORA vía
 * reverse_transfer; "payouts pagados" ya salió a banco (probable pérdida).
 */
function OrgStripeBalance({ orgId }: { orgId: string }) {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const q = useQuery<StripeBalanceResponse>({
    queryKey: ['admin', 'org-stripe-balance', orgId],
    queryFn: () => api(`/admin/orgs/${orgId}/stripe-balance`),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const fmt = (rows: MoneyRow[]) =>
    rows.length === 0
      ? '—'
      : rows
          .map((r) =>
            new Intl.NumberFormat(i18n.language, {
              style: 'currency',
              currency: r.currency.toUpperCase(),
            }).format(r.amountCents / 100),
          )
          .join(' · ');

  if (q.isLoading) return <p className="text-xs text-neutral-500">{tCommon('messages.loading')}</p>;
  if (q.isError || !q.data)
    return <p className="text-xs text-amber-300">{t('admin.orgs_tab.funds.error')}</p>;
  if (!q.data.connected)
    return <p className="text-xs text-neutral-500">{t('admin.orgs_tab.funds.not_connected')}</p>;
  if (q.data.error)
    return <p className="text-xs text-amber-300">{t('admin.orgs_tab.funds.error')}</p>;

  const stats: Array<{ label: string; value: string; hint?: string; className: string }> = [
    {
      label: t('admin.orgs_tab.funds.available'),
      value: fmt(q.data.available),
      hint: t('admin.orgs_tab.funds.available_hint'),
      className: 'text-emerald-300',
    },
    {
      label: t('admin.orgs_tab.funds.pending'),
      value: fmt(q.data.pending),
      className: 'text-neutral-200',
    },
    {
      label: t('admin.orgs_tab.funds.payouts_in_transit'),
      value: fmt(q.data.payoutsInTransit),
      hint: t('admin.orgs_tab.funds.payouts_in_transit_hint'),
      className: 'text-neutral-200',
    },
    {
      label: t('admin.orgs_tab.funds.payouts_paid'),
      value: fmt(q.data.payoutsPaid),
      hint: t('admin.orgs_tab.funds.payouts_paid_hint'),
      className: 'text-neutral-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {stats.map((s) => (
        <div key={s.label} className="rounded border border-neutral-800 bg-neutral-950/40 p-2">
          <p className="text-[10px] uppercase tracking-wider text-neutral-500" title={s.hint}>
            {s.label}
          </p>
          <p className={`text-sm font-semibold ${s.className}`}>{s.value}</p>
        </div>
      ))}
    </div>
  );
}

function OrgsTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [reviewFilter, setReviewFilter] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmSuspendId, setConfirmSuspendId] = useState<string | null>(null);
  const [confirmRejectId, setConfirmRejectId] = useState<string | null>(null);
  const [fundsOrgId, setFundsOrgId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ['admin', 'orgs', debouncedQ, reviewFilter],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ pageSize: '50', page: String(pageParam) });
      if (debouncedQ) params.set('q', debouncedQ);
      if (reviewFilter) params.set('reviewStatus', reviewFilter);
      return api<{ orgs: AdminOrg[]; total: number; hasMore: boolean; page: number }>(
        `/admin/orgs?${params.toString()}`,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    refetchOnWindowFocus: false,
  });
  const orgs = list.data?.pages.flatMap((p) => p.orgs) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;

  const deleteOrg = useMutation({
    mutationFn: (id: string) => api(`/admin/orgs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });

  const suspendOrg = useMutation({
    mutationFn: ({ id, suspend }: { id: string; suspend: boolean }) =>
      api(`/admin/orgs/${id}/${suspend ? 'suspend' : 'unsuspend'}`, { method: 'POST' }),
    onSuccess: () => {
      setConfirmSuspendId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
    },
  });

  const reviewOrg = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'approve' | 'reject' }) =>
      api(`/admin/orgs/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      setConfirmRejectId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'orgs'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('admin.orgs_tab.search_placeholder')}
          className="input flex-1 min-w-[14rem] max-w-md"
        />
        <select
          value={reviewFilter}
          onChange={(e) => setReviewFilter(e.target.value)}
          className="input max-w-[12rem]"
        >
          <option value="">{t('admin.orgs_tab.review_filter_all')}</option>
          <option value="pending">{t('admin.orgs_tab.review_pending')}</option>
          <option value="approved">{t('admin.orgs_tab.review_approved')}</option>
          <option value="rejected">{t('admin.orgs_tab.review_rejected')}</option>
        </select>
      </div>
      {list.isLoading ? (
        <p className="text-neutral-500 text-sm">{tCommon('messages.loading')}</p>
      ) : orgs.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left pb-3 pr-4">{t('admin.orgs_tab.org_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.orgs_tab.owner_col')}</th>
                <th className="text-right pb-3 pr-4">Stripe</th>
                <th className="text-right pb-3 pr-4">{t('admin.orgs_tab.credits_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.orgs_tab.created_col')}</th>
                <th className="pb-3" />
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => (
                <Fragment key={o.id}>
                  <tr className="border-t border-neutral-900 align-top">
                    <td className="py-2 pr-4">
                      <p className="text-neutral-200 font-semibold">
                        {o.name}
                        {o.reviewStatus === 'pending' && (
                          <span className="ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-600 bg-amber-900/40 text-amber-200">
                            {t('admin.orgs_tab.review_pending')}
                          </span>
                        )}
                        {o.reviewStatus === 'rejected' && (
                          <span
                            title={o.reviewNotes ?? undefined}
                            className="ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-red-700 bg-red-900/50 text-red-200"
                          >
                            {t('admin.orgs_tab.review_rejected')}
                          </span>
                        )}
                        {o.suspendedAt && (
                          <span
                            title={o.suspensionReason ?? undefined}
                            className="ml-2 align-middle text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-red-700 bg-red-900/50 text-red-200"
                          >
                            {t('admin.orgs_tab.suspended_badge')}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-neutral-500 font-mono">/{o.slug}</p>
                    </td>
                    <td className="py-2 pr-4 text-neutral-300 break-all">{o.ownerEmail ?? '—'}</td>
                    <td className="py-2 pr-4 text-right">
                      {o.stripeAccountUrl ? (
                        <a
                          href={o.stripeAccountUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={o.stripeAccountId ?? undefined}
                          className={`hover:underline underline-offset-2 ${
                            o.stripeOnboardingComplete ? 'text-emerald-300' : 'text-amber-300'
                          }`}
                        >
                          {o.stripeOnboardingComplete
                            ? t('admin.orgs_tab.stripe_view')
                            : t('admin.orgs_tab.stripe_pending')}{' '}
                          ↗
                        </a>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right text-neutral-300">
                      {o.creditsBalance.toLocaleString()}
                      <span className="text-xs text-neutral-500 ml-1">
                        +{Math.max(0, o.freeMonthlyAllowance - o.freeMonthlyConsumed)}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-neutral-500">
                      {new Date(o.createdAt).toLocaleDateString(i18n.language)}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 justify-end">
                        {/* Aprobar / Rechazar revisión */}
                        {o.reviewStatus !== 'approved' && (
                          <button
                            type="button"
                            disabled={reviewOrg.isPending}
                            onClick={() => reviewOrg.mutate({ id: o.id, action: 'approve' })}
                            className="text-xs px-2 py-1 rounded border border-emerald-800 text-emerald-400 hover:border-emerald-600 hover:text-emerald-300 disabled:opacity-50 transition"
                          >
                            {t('admin.orgs_tab.approve_btn')}
                          </button>
                        )}
                        {o.reviewStatus !== 'rejected' &&
                          (confirmRejectId === o.id ? (
                            <>
                              <button
                                type="button"
                                disabled={reviewOrg.isPending}
                                onClick={() => reviewOrg.mutate({ id: o.id, action: 'reject' })}
                                className="text-xs px-2 py-1 rounded border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-800/60 disabled:opacity-50 transition"
                                title={t('admin.orgs_tab.reject_confirm')}
                              >
                                {reviewOrg.isPending ? '…' : t('admin.orgs_tab.reject_confirm_btn')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmRejectId(null)}
                                className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition"
                              >
                                {t('admin.orgs_tab.reject_cancel')}
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                reviewOrg.reset();
                                setConfirmRejectId(o.id);
                              }}
                              className="text-xs px-2 py-1 rounded border border-red-900 text-red-500 hover:border-red-700 hover:text-red-400 transition"
                            >
                              {t('admin.orgs_tab.reject_btn')}
                            </button>
                          ))}

                        {/* Ver fondos en Stripe (on-demand) */}
                        <button
                          type="button"
                          onClick={() => setFundsOrgId((cur) => (cur === o.id ? null : o.id))}
                          className={`text-xs px-2 py-1 rounded border transition ${
                            fundsOrgId === o.id
                              ? 'border-neutral-500 text-neutral-100'
                              : 'border-neutral-700 text-neutral-400 hover:text-neutral-200'
                          }`}
                        >
                          {t('admin.orgs_tab.funds_btn')}
                        </button>

                        {/* Suspend / Unsuspend (reversible) */}
                        {o.suspendedAt ? (
                          <button
                            type="button"
                            disabled={suspendOrg.isPending}
                            onClick={() => suspendOrg.mutate({ id: o.id, suspend: false })}
                            className="text-xs px-2 py-1 rounded border border-emerald-800 text-emerald-400 hover:border-emerald-600 hover:text-emerald-300 disabled:opacity-50 transition"
                          >
                            {t('admin.orgs_tab.unsuspend_btn')}
                          </button>
                        ) : confirmSuspendId === o.id ? (
                          <>
                            <button
                              type="button"
                              disabled={suspendOrg.isPending}
                              onClick={() => suspendOrg.mutate({ id: o.id, suspend: true })}
                              className="text-xs px-2 py-1 rounded border border-amber-700 bg-amber-900/40 text-amber-200 hover:bg-amber-800/50 disabled:opacity-50 transition"
                              title={t('admin.orgs_tab.suspend_confirm')}
                            >
                              {suspendOrg.isPending ? '…' : t('admin.orgs_tab.suspend_confirm_btn')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmSuspendId(null)}
                              className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition"
                            >
                              {t('admin.orgs_tab.suspend_cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              suspendOrg.reset();
                              setConfirmSuspendId(o.id);
                            }}
                            className="text-xs px-2 py-1 rounded border border-amber-900 text-amber-500 hover:border-amber-700 hover:text-amber-400 transition"
                          >
                            {t('admin.orgs_tab.suspend_btn')}
                          </button>
                        )}

                        {/* Delete (destructivo) */}
                        {confirmId === o.id ? (
                          <>
                            <button
                              type="button"
                              disabled={deleteOrg.isPending}
                              onClick={() => deleteOrg.mutate(o.id)}
                              className="text-xs px-2 py-1 rounded border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-800/60 disabled:opacity-50 transition"
                            >
                              {deleteOrg.isPending ? '…' : t('admin.orgs_tab.delete_confirm_btn')}
                            </button>
                            <button
                              type="button"
                              onClick={() => setConfirmId(null)}
                              className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition"
                            >
                              {t('admin.orgs_tab.delete_cancel')}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              deleteOrg.reset();
                              setConfirmId(o.id);
                            }}
                            className="text-xs px-2 py-1 rounded border border-red-900 text-red-500 hover:border-red-700 hover:text-red-400 transition"
                          >
                            {t('admin.orgs_tab.delete_btn')}
                          </button>
                        )}
                      </span>
                    </td>
                  </tr>
                  {fundsOrgId === o.id && (
                    <tr className="bg-neutral-900/20">
                      <td colSpan={6} className="px-3 pb-3">
                        <OrgStripeBalance orgId={o.id} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {deleteOrg.isError && (
            <p className="text-xs text-red-400 mt-3">
              {(deleteOrg.error as ApiClientError | undefined)?.message ?? 'Error'}
            </p>
          )}
          {suspendOrg.isError && (
            <p className="text-xs text-red-400 mt-3">
              {(suspendOrg.error as ApiClientError | undefined)?.message ?? 'Error'}
            </p>
          )}
          {reviewOrg.isError && (
            <p className="text-xs text-red-400 mt-3">
              {(reviewOrg.error as ApiClientError | undefined)?.message ?? 'Error'}
            </p>
          )}
          <LoadMoreFooter
            label={t('admin.orgs_tab.total', { count: total })}
            hasNextPage={list.hasNextPage}
            isFetchingNextPage={list.isFetchingNextPage}
            onLoadMore={() => list.fetchNextPage()}
          />
        </div>
      ) : (
        <p className="text-neutral-500 text-sm">{tCommon('messages.no_results')}</p>
      )}
    </div>
  );
}

interface AdminEvent {
  id: string;
  slug: string;
  status: 'draft' | 'live' | 'ended' | 'cancelled';
  orgName: string | null;
  orgSlug: string | null;
  title: string;
  startsAt: string;
  createdAt: string;
  currency: string;
}

function EventsTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { lang } = useParams();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ['admin', 'events', debouncedQ, status],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ pageSize: '50', page: String(pageParam) });
      if (debouncedQ) params.set('q', debouncedQ);
      if (status) params.set('status', status);
      return api<{ events: AdminEvent[]; total: number; hasMore: boolean; page: number }>(
        `/admin/events?${params.toString()}`,
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    refetchOnWindowFocus: false,
  });
  const events = list.data?.pages.flatMap((p) => p.events) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;

  const setDraft = useMutation({
    mutationFn: (id: string) =>
      api(`/admin/events/${id}/status`, { method: 'PATCH', body: { status: 'draft' } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'events'] }),
  });

  const deleteEvent = useMutation({
    mutationFn: (id: string) => api(`/admin/events/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmDeleteId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'events'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('admin.events_tab.search_placeholder')}
          className="input flex-1 min-w-[14rem] max-w-md"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input max-w-[12rem]"
        >
          <option value="">{t('admin.events_tab.all_statuses')}</option>
          <option value="draft">{t('event_editor.status_badge.draft')}</option>
          <option value="live">{t('event_editor.status_badge.live')}</option>
          <option value="ended">{t('event_editor.status_badge.ended')}</option>
          <option value="cancelled">{t('event_editor.status_badge.cancelled')}</option>
        </select>
      </div>
      {list.isLoading ? (
        <p className="text-neutral-500 text-sm">{tCommon('messages.loading')}</p>
      ) : events.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left pb-3 pr-4">{t('admin.events_tab.event_col')}</th>
                <th className="text-left pb-3 pr-4">Org</th>
                <th className="text-left pb-3 pr-4">{t('admin.events_tab.status_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.events_tab.starts_col')}</th>
                <th className="pb-3" />
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id} className="border-t border-neutral-900 align-top">
                  <td className="py-2 pr-4">
                    <p className="text-neutral-200 font-semibold">{e.title || e.slug}</p>
                    <p className="text-xs text-neutral-500 font-mono">/{e.slug}</p>
                  </td>
                  <td className="py-2 pr-4 text-neutral-300">
                    {e.orgSlug && e.status === 'live' ? (
                      <Link
                        to={`/${lang}/o/${e.orgSlug}/${e.slug}`}
                        className="hover:text-[var(--color-accent)]"
                        target="_blank"
                        rel="noreferrer"
                      >
                        {e.orgName ?? e.orgSlug}
                      </Link>
                    ) : (
                      (e.orgName ?? e.orgSlug ?? '—')
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <StatusBadge status={e.status} />
                  </td>
                  <td className="py-2 pr-4 text-neutral-500">
                    {new Date(e.startsAt).toLocaleString(i18n.language, {
                      month: 'short',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {confirmDeleteId === e.id ? (
                      <span className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          disabled={deleteEvent.isPending}
                          onClick={() => deleteEvent.mutate(e.id)}
                          className="text-xs px-2 py-1 rounded border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-800/60 disabled:opacity-50 transition"
                        >
                          {deleteEvent.isPending ? '…' : t('admin.events_tab.delete_confirm_btn')}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition"
                        >
                          {t('admin.events_tab.delete_cancel')}
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1">
                        {e.status !== 'draft' && (
                          <button
                            type="button"
                            disabled={setDraft.isPending}
                            onClick={() => setDraft.mutate(e.id)}
                            className="text-xs px-2 py-1 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition disabled:opacity-50"
                          >
                            {t('admin.events_tab.set_draft_btn')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            deleteEvent.reset();
                            setConfirmDeleteId(e.id);
                          }}
                          className="text-xs px-2 py-1 rounded border border-red-900 text-red-500 hover:border-red-700 hover:text-red-400 transition"
                        >
                          {t('admin.events_tab.delete_btn')}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(deleteEvent.isError || setDraft.isError) && (
            <p className="text-xs text-red-400 mt-3">
              {(deleteEvent.error as ApiClientError | undefined)?.message ??
                (setDraft.error as ApiClientError | undefined)?.message ??
                'Error'}
            </p>
          )}
          <LoadMoreFooter
            label={t('admin.events_tab.total', { count: total })}
            hasNextPage={list.hasNextPage}
            isFetchingNextPage={list.isFetchingNextPage}
            onLoadMore={() => list.fetchNextPage()}
          />
        </div>
      ) : (
        <p className="text-neutral-500 text-sm">{tCommon('messages.no_results')}</p>
      )}
    </div>
  );
}

interface AdminOrder {
  id: string;
  eventTitle: string;
  eventSlug: string | null;
  orgName: string | null;
  orgSlug: string | null;
  buyerEmail: string;
  buyerName: string;
  itemsCount: number;
  currency: string;
  totalCents: number;
  status: 'pending' | 'paid' | 'fulfilled' | 'refunded' | 'failed';
  createdAt: string;
  stripePaymentIntentId: string | null;
  stripeUrl: string | null;
}

const orderStatusClass: Record<string, string> = {
  paid: 'text-emerald-300',
  fulfilled: 'text-emerald-300',
  pending: 'text-amber-300',
  refunded: 'text-neutral-400',
  failed: 'text-red-400',
};

/**
 * Unified order-status badge for the admin purchases list.
 *
 * `paid` and `fulfilled` both mean the money was charged — they're sequential
 * steps of the same flow, not parallel outcomes. A healthy order lands on
 * `fulfilled` within milliseconds, so we surface it as a single "Pagada"
 * success badge. An order that stays on `paid` means the charge succeeded but
 * tickets were never issued (QR/email failure, no credits): that's an
 * actionable alert, so we render it as a distinct amber pill.
 */
function AdminOrderStatus({ status }: { status: AdminOrder['status'] }) {
  const { t } = useTranslation('dashboard');
  if (status === 'fulfilled') {
    return (
      <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 whitespace-nowrap">
        {t('admin.orders_tab.status_fulfilled')}
      </span>
    );
  }
  if (status === 'paid') {
    return (
      <span
        title={t('admin.orders_tab.status_paid_alert_title')}
        className="text-xs px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-300 border-amber-500/40 whitespace-nowrap cursor-help"
      >
        ⚠ {t('admin.orders_tab.status_paid_alert')}
      </span>
    );
  }
  return (
    <span className={`text-xs ${orderStatusClass[status] ?? 'text-neutral-400'}`}>
      {t(`sales.order_status.${status}`)}
    </span>
  );
}

function OrdersTab() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const list = useInfiniteQuery({
    queryKey: ['admin', 'orders', debouncedQ, status],
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ pageSize: '50', page: String(pageParam) });
      if (debouncedQ) params.set('q', debouncedQ);
      if (status) params.set('status', status);
      return api<{
        orders: AdminOrder[];
        total: number;
        hasMore: boolean;
        page: number;
        totalsByCurrency: Array<{ currency: string; totalCents: number }>;
      }>(`/admin/orders?${params.toString()}`);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    refetchOnWindowFocus: false,
  });
  const orders = list.data?.pages.flatMap((p) => p.orders) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  // El total acumulado por moneda abarca TODAS las órdenes que matchean el
  // filtro (no solo la página actual), así que es idéntico en cada página:
  // lo tomamos de la primera.
  const totalsByCurrency = list.data?.pages[0]?.totalsByCurrency ?? [];

  const fmtMoney = (cents: number, currency: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('admin.orders_tab.search_placeholder')}
          className="input flex-1 min-w-[14rem] max-w-md"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="input max-w-[12rem]"
        >
          <option value="">{t('admin.orders_tab.all_statuses')}</option>
          <option value="fulfilled">{t('admin.orders_tab.status_fulfilled')}</option>
          <option value="paid">{t('admin.orders_tab.status_paid_alert')}</option>
          <option value="pending">{t('sales.order_status.pending')}</option>
          <option value="refunded">{t('sales.order_status.refunded')}</option>
          <option value="failed">{t('sales.order_status.failed')}</option>
        </select>
      </div>
      {list.isLoading ? (
        <p className="text-neutral-500 text-sm">{tCommon('messages.loading')}</p>
      ) : orders.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left pb-3 pr-4">{t('admin.orders_tab.event_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.orders_tab.buyer_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.orders_tab.date_col')}</th>
                <th className="text-left pb-3 pr-4">{t('admin.orders_tab.status_col')}</th>
                <th className="text-right pb-3 pr-4">{t('admin.orders_tab.total_col')}</th>
                <th className="text-right pb-3">{t('admin.orders_tab.stripe_col')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-neutral-900 align-top">
                  <td className="py-2 pr-4">
                    <p className="text-neutral-200">{o.eventTitle}</p>
                    <p className="text-xs text-neutral-500">{o.orgName ?? '—'}</p>
                  </td>
                  <td className="py-2 pr-4">
                    <p className="text-neutral-300 break-all">{o.buyerEmail}</p>
                    <p className="text-xs text-neutral-500">
                      {t('admin.orders_tab.items_count', { count: o.itemsCount })}
                    </p>
                  </td>
                  <td className="py-2 pr-4 text-neutral-500 whitespace-nowrap">
                    {fmtDateTime(o.createdAt)}
                  </td>
                  <td className="py-2 pr-4">
                    <AdminOrderStatus status={o.status} />
                  </td>
                  <td className="py-2 pr-4 text-right text-neutral-200 font-medium whitespace-nowrap">
                    {fmtMoney(o.totalCents, o.currency)}
                  </td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {o.stripeUrl ? (
                      <a
                        href={o.stripeUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-[var(--color-accent)] hover:underline underline-offset-2"
                      >
                        {t('admin.orders_tab.stripe_link')} ↗
                      </a>
                    ) : (
                      <span className="text-neutral-600 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {totalsByCurrency.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-neutral-800">
                  <td
                    colSpan={4}
                    className="pt-3 text-xs uppercase tracking-wider text-neutral-500 text-right pr-4"
                  >
                    {t('admin.orders_tab.accumulated_total')}
                  </td>
                  <td colSpan={2} className="pt-3 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      {totalsByCurrency.map((tot) => (
                        <span key={tot.currency} className="text-neutral-100 font-semibold">
                          {fmtMoney(tot.totalCents, tot.currency)}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          <LoadMoreFooter
            label={t('admin.orders_tab.total_label', { count: total })}
            hasNextPage={list.hasNextPage}
            isFetchingNextPage={list.isFetchingNextPage}
            onLoadMore={() => list.fetchNextPage()}
          />
        </div>
      ) : (
        <p className="text-neutral-500 text-sm">{tCommon('messages.no_results')}</p>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: 'draft' | 'live' | 'ended' | 'cancelled' }) {
  const { t } = useTranslation('dashboard');
  const classByStatus: Record<typeof status, string> = {
    draft: 'bg-neutral-700/40 text-neutral-300 border-neutral-700',
    live: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    ended: 'bg-neutral-900 text-neutral-500 border-neutral-800',
    cancelled: 'bg-red-500/10 text-red-300 border-red-500/30',
  };
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded-full border whitespace-nowrap ${classByStatus[status]}`}
    >
      {t(`event_editor.status_badge.${status}`)}
    </span>
  );
}

interface UserDetailData {
  id: string;
  email: string;
  name: string;
  locale: string;
  country: string | null;
  emailVerifiedAt: string | null;
  isAdmin: boolean;
  createdAt: string;
  orgs: Array<{
    id: string;
    name: string;
    slug: string;
    stripeOnboardingComplete: boolean;
    creditsBalance: number;
    country: string;
  }>;
  purchases: Array<{
    id: string;
    eventTitle: string;
    currency: string;
    totalCents: number;
    status: string;
    createdAt: string;
  }>;
  salesSummary: Array<{
    eventId: string;
    eventTitle: string;
    eventStatus: string;
    ordersCount: number;
    totalCents: number;
    currency: string;
  }>;
}

function UserDetail() {
  const { t, i18n } = useTranslation('dashboard');
  const { t: tCommon } = useTranslation('common');
  const { lang, userId } = useParams();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detail = useQuery<UserDetailData>({
    queryKey: ['admin', 'users', userId],
    queryFn: () => api<UserDetailData>(`/admin/users/${userId}`),
    enabled: Boolean(userId),
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/admin/users/${userId}`, { method: 'DELETE' }),
    onSuccess: () => navigate(`/${lang}/dashboard/admin/users`, { replace: true }),
  });

  if (detail.isLoading) {
    return <p className="text-neutral-500 text-sm py-6">{tCommon('messages.loading')}</p>;
  }
  if (detail.isError || !detail.data) {
    return (
      <p className="text-red-400 text-sm py-6">
        {(detail.error as ApiClientError | undefined)?.message ?? 'Error loading user'}
      </p>
    );
  }

  const u = detail.data;
  const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(i18n.language);
  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  const fmtMoney = (cents: number, currency: string) =>
    new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);

  return (
    <div className="space-y-6">
      {/* Back */}
      <Link
        to={`/${lang}/dashboard/admin/users`}
        className="text-sm text-neutral-400 hover:text-neutral-200 transition"
      >
        {t('admin.user_detail.back')}
      </Link>

      {/* Profile card */}
      <div className="card space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xl font-bold text-neutral-100 break-all">{u.email}</p>
            {u.name && <p className="text-neutral-400 text-sm mt-0.5">{u.name}</p>}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {u.isAdmin && (
              <span className="text-xs px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30">
                Admin
              </span>
            )}
            <span
              className={`text-xs px-2 py-0.5 rounded-full border ${
                u.emailVerifiedAt
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-800'
                  : 'bg-amber-500/10 text-amber-400 border-amber-800'
              }`}
            >
              {u.emailVerifiedAt
                ? t('admin.user_detail.verified')
                : t('admin.user_detail.not_verified')}
            </span>
            {me?.id !== u.id && (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="text-xs px-2 py-1 rounded border border-red-800 text-red-400 hover:bg-red-900/30 transition"
              >
                {t('admin.user_detail.delete_btn')}
              </button>
            )}
          </div>
        </div>

        {/* Inline delete confirmation */}
        {confirmDelete && (
          <div className="mt-3 p-3 rounded-lg border border-red-800/60 bg-red-950/40 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-red-300 flex-1">
              {u.orgs.length > 0
                ? t('admin.user_detail.delete_has_orgs')
                : t('admin.user_detail.delete_confirm')}
            </p>
            {u.orgs.length === 0 && (
              <button
                type="button"
                disabled={deleteMutation.isPending}
                onClick={() => deleteMutation.mutate()}
                className="text-xs px-3 py-1.5 rounded border border-red-700 bg-red-900/50 text-red-200 hover:bg-red-800/60 disabled:opacity-50 transition whitespace-nowrap"
              >
                {deleteMutation.isPending ? '…' : t('admin.user_detail.delete_confirm_btn')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="text-xs px-3 py-1.5 rounded border border-neutral-700 text-neutral-400 hover:text-neutral-200 transition whitespace-nowrap"
            >
              {t('admin.user_detail.delete_cancel')}
            </button>
          </div>
        )}

        {deleteMutation.isError && (
          <p className="text-sm text-red-400 mt-2">
            {(deleteMutation.error as ApiClientError | undefined)?.message ?? 'Error'}
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-neutral-900 text-sm">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">
              {t('admin.user_detail.joined')}
            </p>
            <p className="text-neutral-300 mt-0.5">{fmtDate(u.createdAt)}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">
              {t('admin.user_detail.locale_label')}
            </p>
            <p className="text-neutral-300 mt-0.5 uppercase">{u.locale}</p>
          </div>
          {u.country && (
            <div>
              <p className="text-xs text-neutral-500 uppercase tracking-wider">
                {t('admin.user_detail.country_label')}
              </p>
              <p className="text-neutral-300 mt-0.5 uppercase">{u.country}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider">ID</p>
            <p className="text-neutral-500 mt-0.5 font-mono text-xs break-all">{u.id}</p>
          </div>
        </div>
      </div>

      {/* Organizations */}
      <section>
        <h2 className="text-sm font-semibold text-neutral-300 uppercase tracking-wider mb-3">
          {t('admin.user_detail.orgs_title')}
        </h2>
        {u.orgs.length === 0 ? (
          <p className="text-neutral-500 text-sm">{t('admin.user_detail.no_orgs')}</p>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left pb-3 pr-4">Org</th>
                  <th className="text-left pb-3 pr-4">Stripe</th>
                  <th className="text-right pb-3 pr-4">{t('admin.orgs_tab.credits_col')}</th>
                  <th className="text-left pb-3">{t('admin.user_detail.country_label')}</th>
                </tr>
              </thead>
              <tbody>
                {u.orgs.map((o) => (
                  <tr key={o.id} className="border-t border-neutral-900">
                    <td className="py-2 pr-4">
                      <p className="text-neutral-200 font-semibold">{o.name}</p>
                      <p className="text-xs text-neutral-500 font-mono">/{o.slug}</p>
                    </td>
                    <td className="py-2 pr-4">
                      {o.stripeOnboardingComplete ? (
                        <span className="text-emerald-300 text-xs">
                          {t('admin.user_detail.stripe_ok')}
                        </span>
                      ) : (
                        <span className="text-amber-400 text-xs">
                          {t('admin.user_detail.stripe_pending')}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right text-neutral-300">
                      {o.creditsBalance.toLocaleString()}
                    </td>
                    <td className="py-2 text-neutral-400 uppercase text-xs">{o.country}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tickets sold (sales summary by event) */}
      <section>
        <h2 className="text-sm font-semibold text-neutral-300 uppercase tracking-wider mb-3">
          {t('admin.user_detail.sales_title')}
        </h2>
        {u.salesSummary.length === 0 ? (
          <p className="text-neutral-500 text-sm">{t('admin.user_detail.no_sales')}</p>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left pb-3 pr-4">{t('admin.events_tab.event_col')}</th>
                  <th className="text-left pb-3 pr-4">{t('admin.events_tab.status_col')}</th>
                  <th className="text-right pb-3 pr-4">{t('admin.user_detail.orders_col')}</th>
                  <th className="text-right pb-3">{t('admin.user_detail.revenue_col')}</th>
                </tr>
              </thead>
              <tbody>
                {u.salesSummary.map((s) => (
                  <tr key={s.eventId} className="border-t border-neutral-900">
                    <td className="py-2 pr-4">
                      <p className="text-neutral-200">{s.eventTitle}</p>
                      <p className="text-xs text-neutral-500 font-mono">{s.eventId.slice(-8)}</p>
                    </td>
                    <td className="py-2 pr-4">
                      {['draft', 'live', 'ended', 'cancelled'].includes(s.eventStatus) ? (
                        <StatusBadge
                          status={s.eventStatus as 'draft' | 'live' | 'ended' | 'cancelled'}
                        />
                      ) : (
                        <span className="text-neutral-500 text-xs">{s.eventStatus}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right text-neutral-300">{s.ordersCount}</td>
                    <td className="py-2 text-right text-neutral-200 font-medium">
                      {fmtMoney(s.totalCents, s.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tickets purchased */}
      <section>
        <h2 className="text-sm font-semibold text-neutral-300 uppercase tracking-wider mb-3">
          {t('admin.user_detail.purchases_title')}
        </h2>
        {u.purchases.length === 0 ? (
          <p className="text-neutral-500 text-sm">{t('admin.user_detail.no_purchases')}</p>
        ) : (
          <div className="card overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-neutral-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left pb-3 pr-4">{t('admin.events_tab.event_col')}</th>
                  <th className="text-left pb-3 pr-4">{t('admin.user_detail.date_col')}</th>
                  <th className="text-left pb-3 pr-4">{t('admin.user_detail.status_col')}</th>
                  <th className="text-right pb-3">{t('admin.user_detail.total_col')}</th>
                </tr>
              </thead>
              <tbody>
                {u.purchases.map((p) => (
                  <tr key={p.id} className="border-t border-neutral-900">
                    <td className="py-2 pr-4 text-neutral-200">{p.eventTitle}</td>
                    <td className="py-2 pr-4 text-neutral-500 whitespace-nowrap">
                      {fmtDateTime(p.createdAt)}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`text-xs ${orderStatusClass[p.status] ?? 'text-neutral-400'}`}
                      >
                        {p.status}
                      </span>
                    </td>
                    <td className="py-2 text-right text-neutral-200 font-medium">
                      {fmtMoney(p.totalCents, p.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
